import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import {
	AccessTokens,
	ServerDataResponse,
	User,
	UserLoginData
} from '@models/models';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

export enum OauthClient {
	SN_MOBILE = 'Basic U05fTU9CSUxFOnNlY3JldA==',
	WEB = 'Basic U05fV0VCOndlYlBhc3M=',
	WEB_STLG = 'Basic U05fV0VCX1NUTEc6d2ViUGFzcw=='
}

@Injectable()
export class AccountDataServiceProvider {
	private headers: HttpHeaders;
	constructor(
		public http: HttpClient,
		private location: Location,
		private stringModifierService: StringModifierServiceProvider,
		private brandsDataService: BrandsDataServiceProvider
	) {
		this.headers = new HttpHeaders().set(
			'Content-Type',
			'application/x-www-form-urlencoded'
		);
	}

	logout(): Observable<any> {
		const url = '/oauth/revoke';
		return this.http.post(url, {});
	}

	getUser(): Observable<User> {
		const url = '/api/basic/v1/profile';

		return this.http
			.get(url, { headers: this.headers })
			.pipe(map((res: ServerDataResponse<User>): User => res.data));
	}

	getCompany(): Observable<any> {
		const url = '/protected/rest/company';
		return this.http.get(url);
	}

	getAvatar(): Observable<any> {
		const url = '/api/user/v1/avatar';
		return this.http.get(url);
	}

	authenticateMobile(
		email: string,
		password: string,
		companyId: number
	): Observable<any> {
		const url = '/oauth/token';
		const headers = new HttpHeaders().set(
			'Authorization',
			OauthClient.SN_MOBILE
		);

		const formData: FormData = new FormData();
		formData.append('username', email);
		formData.append('password', password);
		formData.append('grant_type', 'password');

		if (companyId) {
			formData.append('companyId', companyId.toString());
		}

		return this.http.post(url, formData, { headers });
	}

	authenticateWeb(
		email: string,
		password: string,
		stayLogin: boolean
	): Observable<any> {
		const url = '/oauth/token';
		let headers = new HttpHeaders();

		if (stayLogin) {
			headers = headers.append('Authorization', OauthClient.WEB_STLG);
		} else {
			headers = headers.append('Authorization', OauthClient.WEB);
		}
		const formData: FormData = new FormData();
		formData.append('username', email);
		formData.append('password', password);
		formData.append('grant_type', 'password');

		localStorage.setItem('PhotographerEmail', email);
		const companyId = this.brandsDataService.getActiveBrandId();
		if (companyId) {
			formData.append('companyId', companyId);
		}

		return this.http.post(url, formData, { headers });
	}

	authenticateWithTwoFa(
		data: UserLoginData,
		authorizationKey: string
	): Observable<AccessTokens> {
		const url = '/oauth/token';
		const formData: FormData = new FormData();
		let headers: HttpHeaders = new HttpHeaders();

		headers = headers.append('Authorization', authorizationKey);

		Object.keys(data).forEach((key: string): void => {
			formData.append(key, data[key]);
		});

		return this.http.post(url, formData, { headers }) as Observable<
			AccessTokens
		>;
	}

	signUp(signUpData): Observable<any> {
		const url = '/public/rest/user/signup';
		return this.http.post(url, signUpData);
	}

	forgotPassword(email: string): Observable<any> {
		const url = '/public/rest/user/forgotpassword';
		const body = this.stringModifierService.urlEncodDataTransform({ email });
		return this.http.post(url, body, { headers: this.headers });
	}

	confirmSignUp(url: string): Observable<any> {
		const restPathUrl = '/public/rest/signup/confirm/';
		const confirmPathPart = 'confirm/';
		const index = url.indexOf(confirmPathPart);

		if (index && index + confirmPathPart.length < url.length) {
			url = url.substring(confirmPathPart.length + index, url.length);
			url = restPathUrl + url;
		} else {
			url = null;
		}

		if (url !== null) {
			return this.http.get(url);
		}
		return null;
	}

	getUserSubscription(): Observable<any> {
		const url = '/protected_base/rest/subscription';
		return this.http.get(url, { headers: this.headers });
	}

	resendConfirmEmail(email: string): Observable<any> {
		const url = `/public/rest/signup/resend/confirm/${email}`;
		return this.http.post(url, {});
	}

	getInvitationCompany(token: string): Observable<any> {
		const url = `/public/rest/user/signup/${token}`;
		return this.http.get(url);
	}
	getRefreshedAuthTokenWeb(token: string) {
		const url = '/oauth/token';
		const params: HttpParams = new HttpParams()
			.set('grant_type', 'refresh_token')
			.set('refresh_token', token);
		const logIn = localStorage.getItem('isStayLogin');
		if (logIn === null) {
			this.location.go('/login');
		} else if (logIn === 'true') {
			const headers = new HttpHeaders().set(
				'Authorization',
				OauthClient.WEB_STLG
			);
			return this.http.post(url, {}, { params, headers });
		} else {
			const headers = new HttpHeaders().set('Authorization', OauthClient.WEB);
			return this.http.post(url, {}, { params, headers });
		}
	}

	getRefreshedAuthToken(token: string) {
		const url = '/oauth/token';
		const params: HttpParams = new HttpParams()
			.set('grant_type', 'refresh_token')
			.set('refresh_token', token);
		const headers = new HttpHeaders().set(
			'Authorization',
			OauthClient.SN_MOBILE
		);

		return this.http.post(url, {}, { params, headers });
	}

	acceptTermsOfUse(): Observable<any> {
		const url = '/api/user/termsofuse/accept';
		return this.http.put(url, {});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/user/account.data.service.ts